import { useMemo, useState, useCallback, useEffect, useContext } from 'react'
import styled from '@emotion/styled'
import { TextField, FormControlLabel, Checkbox, FormGroup } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import { number, oneOfType, string, func, object, bool } from 'prop-types'
import { useSelector } from 'react-redux'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { CvsAutoComplete, CvsButton } from '@components'
import { fieldSetContext } from '@contexts'
import { FieldGovernanceTypes as types } from '@global/constants'
import { useAppSelector } from '@redux/hooks'
import { getCalculatedOriginFieldId } from './calculated/utils'
import {
  Autofill,
  Concat,
  Constant,
  Date,
  List,
  Text,
  AutonumberDetails,
  TemplateFieldPattern,
  ContentComprehension,
  ParentTemplateField,
} from './components'
import { SplitOriginGovernanceTypes as splitTransformSourceTypes } from './split-actions'
import { t } from '@lingui/core/macro'

const componentTypes = {
  [types.Autonumber]: AutonumberDetails,
  [types.Autofill]: Autofill,
  [types.Concatenation]: Concat,
  [types.Constant]: Constant,
  [types.Date]: Date,
  [types.List]: List,
  [types.Text]: Text,
  [types['Content Comprehension']]: ContentComprehension,
}

const baseTypes = {
  autonumber: null,
  autofill: null,
  list_governance: null,
  concatenation_governance: null,
  date: null,
  max_length: null,
  min_length: null,
  constant_value: null,
  transformation: null,
  validation: null,
}

const StyledForm = styled.form`
  padding: ${({ theme }) => theme.spacing(2)};
`

const StyledButton = styled(CvsButton)`
  margin-top: 12px;
  width: 100%;
`

const defaultValues = {
  name: '',
  description: '',
  checked: false,
  governance_type: '',
}

export const FieldGovernance = ({
  fieldSetId,
  fieldId,
  submit,
  close,
  field,
  isTemplateField = false,
}) => {
  const { byId } = useContext(fieldSetContext)
  const [form, setForm] = useState({ ...defaultValues, ...field, fieldset_id: fieldSetId })
  const [
    isUnsupportedSourceFieldTypeForSplitTransform,
    setIsUnsupportedSourceFieldTypeForSplitTransform,
  ] = useState(false)

  const [CREATIVE_CLASSIFICATION_ENABLED = false, DATA_RELATIONSHIP_ENABLED = false] =
    useAppSelector(
      selectFeatureIncludes([FeatureDict.CREATIVE_CLASSIFICATION, FeatureDict.DATA_RELATIONSHIP]),
    )

  const TypeFormComponent = useMemo(() => {
    return componentTypes[form.governance_type]
  }, [form.governance_type, componentTypes])

  const isCurrentFieldUsedInSplitTransform = () => {
    const isNewField = field.id === null || field.id === undefined
    const fieldsetFields = byId[fieldSetId]?.fields
    const shouldValidateField = splitTransformSourceTypes.includes(field.governance_type)
    // If it's a pattern field, fieldsetFields will be undefined
    if (fieldsetFields && fieldsetFields.length > 0 && !isNewField && shouldValidateField) {
      return fieldsetFields.some(fieldsetField => {
        return getCalculatedOriginFieldId(fieldsetField) === field.id
      })
    }

    return false
  }

  useEffect(() => {
    if (isGovernanceType('autonumber') || isGovernanceType('creative')) {
      setForm(prev => ({ ...prev, required: false }))
    }

    /**
     * When changing the field type, we need to check if the current field is being used
     * as a Source field for a Split and Extract transform. If so, we need to validate if the
     * new governance type is accepted by the transform.
     */
    if (
      isCurrentFieldUsedInSplitTransform() &&
      !splitTransformSourceTypes.includes(form.governance_type)
    ) {
      setIsUnsupportedSourceFieldTypeForSplitTransform(true)
    } else {
      setIsUnsupportedSourceFieldTypeForSplitTransform(false)
    }
  }, [form.governance_type])

  const typeOptions = useMemo(
    () =>
      Object.keys(types)
        .map(key => ({ value: types[key], label: key }))
        .filter(opt => {
          return field.invalidTypes ? !field.invalidTypes[opt.value] : true
        })
        /**
         * if CREATIVE_CLASSIFICATION_ENABLED is false, remove the 'creative' (Content Comprehension) type from the options
         */
        .filter(opt =>
          CREATIVE_CLASSIFICATION_ENABLED ? true : opt.value !== types['Content Comprehension'],
        ),
    [],
  )
  const onSubmit = useCallback(
    e => {
      e.preventDefault()
      close()
      return submit(form)
    },
    [form, submit, close],
  )

  const isValidSplitTransform = () => {
    // TODO: Eventually we will have to check for each entry. For V1 we are
    // only using one transformation per field
    if (form.transformation?.calculated && form.transformation?.calculated.length > 0) {
      if (
        form.transformation?.calculated[0]?.origin_field_id !== '' &&
        (form.transformation?.calculated[0]?.arguments[0] === '' ||
          form.transformation?.calculated[0]?.arguments[1] === '')
      ) {
        return false
      }
      if (
        form.transformation?.calculated[0]?.origin_field_id === '' &&
        (form.transformation?.calculated[0]?.arguments[0] !== '' ||
          form.transformation?.calculated[0]?.arguments[1] !== '')
      ) {
        return false
      }
      if (
        (form.transformation?.calculated[0]?.func &&
          form.transformation?.calculated[0]?.arguments[0] === '') ||
        form.transformation?.calculated[0]?.arguments[1] === '' ||
        form.transformation?.calculated[0]?.origin_field_id === ''
      ) {
        return false
      }
    }

    return true
  }

  const isButtonDisabled = () => {
    if (!form.name || !form.governance_type) {
      return true
    }
    if (isUnsupportedSourceFieldTypeForSplitTransform) {
      return true
    }
    if (form.governance_type === 'list') {
      if (!form.list_governance || !form.list_governance.lists) {
        return true
      }
      if (form.list_governance.multi && !form.list_governance.delimiter) {
        return true
      }
      if (
        (form.list_governance.hierarchy && !form.list_governance.indicator) ||
        (form.list_governance.multi &&
          form.list_governance.hierarchy &&
          form.list_governance.delimiter === form.list_governance.indicator)
      ) {
        return true
      }
      if (!form.list_governance.list_id) {
        return true
      }
      if (!isValidSplitTransform()) {
        return true
      }
      if (form.list_governance && form.list_governance.lists.type !== 'drop-down') {
        if (form.list_governance.list_column_id) {
          return false
        }
        return true
      }

      return false
    }

    if (form.governance_type === 'autofill') {
      if (!form.autofill?.field_id) {
        return true
      }

      return false
    }

    if (form.governance_type === 'date') {
      if (!form.date.order || !form.date.months || !form.date.days || !form.date.years) {
        return true
      }

      if (!isValidSplitTransform()) {
        return true
      }
    }

    if (form.governance_type === 'autonumber') {
      if (
        !form?.autonumber?.min_width ||
        !form?.autonumber?.start_value ||
        form?.autonumber?.min_width === 0 ||
        form?.autonumber?.start_value === 0 ||
        form?.autonumber?.min_width < form?.autonumber?.start_value?.length
      ) {
        return true
      }
    }

    if (form.governance_type === 'text') {
      if (form.validation?.suggestion_list?.toString() === 'true') {
        // This means that the user checked the box, but didn't choose any list
        return true
      }

      /** TODO: Determine if this check is needed due to custom delimiters */
      if (!isValidSplitTransform()) {
        return true
      }
    }

    return false
  }

  const isRequiredChecked = isGovernanceType('autonumber') ? false : Boolean(form.required)
  const isUniquenessRequiredChecked =
    isGovernanceType('text') || isGovernanceType('concatenation')
      ? Boolean(form.must_be_unique)
      : false

  const isSysGenerated =
    field?.governance_type === 'qr-code' ||
    field?.governance_type === 'shortened link' ||
    field?.governance_type === 'deep-link' ||
    field?.governance_type === 'custom-bitlink' ||
    field?.governance_type === 'content comprehension'

  const props = isGovernanceType('autonumber')
    ? {
        startValue: form?.autonumber?.start_value,
        minWidth: form?.autonumber?.min_width,
        updater: setForm,
      }
    : {
        form,
        setForm,
        fieldSetId,
        disabled: field?.disabledFields?.governance,
        fieldId,
      }

  const showRequireUniqueness = useSelector(state => state.Auth.features['require-uniqueness'])

  return (
    <StyledForm onSubmit={onSubmit}>
      <FormGroup sx={{ gap: '5px' }}>
        <TextField
          margin="dense"
          variant="outlined"
          required
          disabled={field?.disabledFields?.name}
          placeholder={t`Name`}
          value={form.name || ''}
          onChange={e => handleChangeTextInput(e, 'name')}
          label={t`Name`}
          name="field-name"
        />
        <Tooltip title={(isSysGenerated && form.description) || ''}>
          <TextField
            margin="dense"
            variant="outlined"
            placeholder={t`Description`}
            disabled={field?.disabledFields?.description || isGovernanceType('deep-link')}
            value={form.description || ''}
            onChange={e => handleChangeTextInput(e, 'description')}
            label={t`Description`}
            name="field-description"
          />
        </Tooltip>

        {!isSysGenerated && (
          <>
            <FormControlLabel
              margin="dense"
              control={
                <Checkbox
                  name="field-required"
                  checked={isRequiredChecked}
                  disabled={
                    field?.disabledFields?.required ||
                    isGovernanceType('autonumber') ||
                    isGovernanceType('creative')
                  }
                  onChange={handleClickRequired}
                />
              }
              label={t`Required field`}
            />
            {showRequireUniqueness &&
              (isGovernanceType('text') || isGovernanceType('concatenation')) && (
                <FormControlLabel
                  margin="dense"
                  control={
                    <Checkbox
                      name="require-uniqueness"
                      checked={isUniquenessRequiredChecked}
                      disabled={field?.disabledFields?.must_be_unique}
                      onChange={handleUniquenessRequired}
                    />
                  }
                  label={t`Require uniqueness`}
                />
              )}
            <CvsAutoComplete
              name="field-type"
              margin="dense"
              label={t`Type`}
              disabled={field?.disabledFields?.type}
              options={typeOptions}
              required={true}
              onSelected={value => {
                let overwrite
                switch (value) {
                  case 'list':
                    overwrite = { ...baseTypes, list_governance: { lists: {} } }
                    break
                  case 'autonumber':
                    overwrite = {
                      ...baseTypes,
                      autonumber: {
                        start_value: 1,
                        min_width: 1,
                      },
                    }
                    break
                  case 'autofill':
                    overwrite = { ...baseTypes, transformation: {}, autofill: {} }
                    break
                  case 'text':
                    overwrite = {
                      ...baseTypes,
                      transformation: {},
                      validation: {},
                    }
                    break
                  case 'concatenation':
                    overwrite = {
                      ...baseTypes,
                      concatenation_governance: { concatenation_governance_patterns: [] },
                    }
                    break
                  case 'constant':
                    overwrite = { ...baseTypes, constant_value: '' }
                    break
                  case 'date':
                    overwrite = { ...baseTypes, date: {} }
                    break
                  case 'creative':
                    overwrite = { ...baseTypes, classification: { category_id: null } }
                    break
                  default:
                    overwrite = baseTypes
                }

                return setForm(prev => ({ ...prev, ...overwrite, governance_type: value }))
              }}
              selected={form.governance_type || ''}
            />
            {isTemplateField && DATA_RELATIONSHIP_ENABLED && (
              <ParentTemplateField form={form} setForm={setForm} />
            )}
            {TypeFormComponent && <TypeFormComponent {...props} />}
            {isTemplateField && <TemplateFieldPattern fieldId={fieldId} form={form} />}
          </>
        )}
      </FormGroup>
      <Tooltip
        title={
          isUnsupportedSourceFieldTypeForSplitTransform
            ? t`Split and Extract transform does not support this type of field.`
            : ''
        }
      >
        <span>
          <StyledButton
            disabled={isButtonDisabled()}
            variant="contained"
            color="secondary"
            type="submit"
            name="save-field"
          >
            {t`Done`}
          </StyledButton>
        </span>
      </Tooltip>
    </StyledForm>
  )

  function handleChangeTextInput(e, key) {
    const { value } = e.target
    setForm(prev => ({
      ...prev,
      [key]: value,
    }))
  }

  function handleClickRequired(e) {
    const { checked } = e.target
    setForm(prev => ({ ...prev, required: checked }))
  }

  function handleUniquenessRequired(e) {
    const { checked } = e.target
    setForm(prev => ({ ...prev, must_be_unique: checked }))
  }

  function isGovernanceType(governanceType) {
    return form?.governance_type === governanceType
  }
}

FieldGovernance.propTypes = {
  fieldId: oneOfType([string, number]).isRequired,
  fieldSetId: oneOfType([string, number]).isRequired,
  submit: func.isRequired,
  close: func.isRequired,
  field: object.isRequired,
  isTemplateField: bool,
}
